import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

interface CagrChartRow {
  category: string;
  value: number;
}

@Component({
  selector: 'app-cagr-hover-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './cagr-hover-chart.component.html',
  styleUrls: ['./cagr-hover-chart.component.scss'],
})
export class CagrHoverChartComponent {
  @Input() data: any;

  readonly viewBoxWidth = 920;
  readonly viewBoxHeight = 470;
  readonly plotLeft = 108;
  readonly plotRight = 54;
  readonly plotTop = 76;
  readonly plotBottom = 56;
  readonly barWidth = 46;
  readonly axisLineX = this.plotLeft - 72;
  readonly axisTitleX = this.axisLineX - 26;
  readonly axisTitleY = (this.plotTop + this.viewBoxHeight - this.plotBottom) / 2;

  get rows(): CagrChartRow[] {
    const rawRows = this.data?.rows || this.data?.data || [];
    const categoryKey = this.data?.categoryKey || 'year';
    const valueKey = this.data?.valueKey || 'sales';

    if (!Array.isArray(rawRows)) {
      return [];
    }

    return rawRows.map((row: any, index: number) => ({
      category: String(row?.[categoryKey] ?? row?.category ?? index + 1),
      value: this.toNumber(row?.[valueKey] ?? row?.value),
    }));
  }

  get baselineY(): number {
    return this.viewBoxHeight - this.plotBottom;
  }

  get plotWidth(): number {
    return this.viewBoxWidth - this.plotLeft - this.plotRight;
  }

  get maxValue(): number {
    const configuredMax = this.toNumber(this.data?.axis?.max);
    if (configuredMax > 0) {
      return configuredMax;
    }

    const values = this.rows.map((row) => row.value).filter((value) => value > 0);
    if (!values.length) {
      return 1;
    }

    return this.getNiceAxisMax(Math.max(...values) * 1.18);
  }

  get leftTitle(): string {
    return this.data?.leftTitle || 'Sales';
  }

  get cagrLabel(): string {
    return this.data?.cagrLabel || '-35%';
  }

  get showGauge(): boolean {
    return this.gaugeSegments.length > 0 && this.gaugeScaleLabels.length > 1;
  }

  get gaugeValue(): number {
    const rawValue = Number(this.data?.progressValue ?? 0);
    return Number.isFinite(rawValue) ? rawValue : 0;
  }

  get gaugeValueLabel(): string {
    return this.data?.progressValueLabel || this.cagrLabel;
  }

  get gaugeMin(): number {
    const rawValue = Number(this.data?.gaugeMin ?? -20);
    return Number.isFinite(rawValue) ? rawValue : -20;
  }

  get gaugeMax(): number {
    const rawValue = Number(this.data?.gaugeMax ?? 20);
    return Number.isFinite(rawValue) && rawValue > this.gaugeMin ? rawValue : 20;
  }

  get gaugeProgress(): number {
    const span = this.gaugeMax - this.gaugeMin;
    if (!(span > 0)) {
      return 0;
    }

    return Math.max(0, Math.min(((this.gaugeValue - this.gaugeMin) / span) * 100, 100));
  }

  get gaugeSegments(): Array<{ tone: string; size: number }> {
    const segments = Array.isArray(this.data?.gaugeSegments) ? this.data.gaugeSegments : [];
    return segments
      .map((segment: any) => ({
        tone: String(segment?.tone || '').trim().toLowerCase(),
        size: Number(segment?.size),
      }))
      .filter((segment: { tone: string; size: number }) => Boolean(segment.tone) && Number.isFinite(segment.size) && segment.size > 0);
  }

  get gaugeTrackStyle(): Record<string, string> {
    return {
      'grid-template-columns': this.gaugeSegments.map((segment) => `${segment.size}fr`).join(' '),
    };
  }

  get gaugeScaleLabels(): Array<{ text: string; left: number; edgeClass: string }> {
    const labels = Array.isArray(this.data?.gaugeScaleLabels) ? this.data.gaugeScaleLabels : [];
    return labels
      .map((label: any) => {
        const text = String(label?.label || '').trim();
        const left = Number(label?.position);
        if (!text || !Number.isFinite(left)) {
          return null;
        }

        const safeLeft = Math.max(0, Math.min(100, left));
        return {
          text,
          left: safeLeft,
          edgeClass: safeLeft <= 0 ? 'start' : safeLeft >= 100 ? 'end' : 'middle',
        };
      })
      .filter((label: { text: string; left: number; edgeClass: string } | null): label is { text: string; left: number; edgeClass: string } => Boolean(label));
  }

  get gaugeMarkerStyle(): Record<string, string> {
    return {
      left: `${this.gaugeProgress}%`,
    };
  }

  get gaugeValueStyle(): Record<string, string> {
    return {
      left: `${this.gaugeProgress}%`,
    };
  }

  get barColor(): string {
    return this.data?.barColor || '#c8191e';
  }

  get bracketColor(): string {
    return this.data?.bracketColor || '#6d6d6d';
  }

  get firstBarCenterX(): number {
    return this.xForIndex(this.firstNonZeroIndex);
  }

  get lastBarCenterX(): number {
    return this.xForIndex(this.lastNonZeroIndex);
  }

  get firstNonZeroIndex(): number {
    const index = this.rows.findIndex((row) => row.value > 0);
    return index >= 0 ? index : 0;
  }

  get lastNonZeroIndex(): number {
    for (let index = this.rows.length - 1; index >= 0; index -= 1) {
      if (this.rows[index]?.value > 0) {
        return index;
      }
    }

    return Math.max(this.rows.length - 1, 0);
  }

  get bracketY(): number {
    return 60;
  }

  get bracketDropY(): number {
    return 102;
  }

  get arrowHeadHeight(): number {
    return Number(this.data?.arrowHeadHeight ?? 16);
  }

  get arrowHalfWidth(): number {
    return Number(this.data?.arrowHalfWidth ?? 9);
  }

  get arrowStemTopY(): number {
    return this.arrowPointY - this.arrowHeadHeight;
  }

  get arrowPointY(): number {
    const defaultArrowPointY = Number(this.data?.arrowPointY ?? 202);
    const minArrowY = Number(this.data?.arrowMinY ?? this.bracketDropY + 14);
    const lastRow = this.rows[this.lastNonZeroIndex];
    const lastBarTop = lastRow ? this.barTop(lastRow.value) : this.baselineY;
    const lastBarHeight = lastRow ? this.barHeight(lastRow.value) : 0;
    const arrowBarClearance = Number(
      this.data?.arrowBarClearance ?? Math.max(48, Math.min(lastBarHeight * 0.18, 72)),
    );
    const valueLabelBaselineOffset = Number(this.data?.valueLabelBaselineOffset ?? 8);
    const valueLabelFontSize = Number(this.data?.valueLabelFontSize ?? 20);
    const valueLabelClearance = Number(this.data?.arrowValueLabelClearance ?? 10);
    const lastValueLabelY = lastBarTop - valueLabelBaselineOffset;
    const lastValueLabelTopY = lastValueLabelY - valueLabelFontSize;
    const maxArrowPointY = Math.min(
      lastBarTop - arrowBarClearance,
      lastValueLabelTopY - valueLabelClearance,
    );

    return Math.max(
      minArrowY,
      Math.min(defaultArrowPointY, maxArrowPointY),
    );
  }

  xForIndex(index: number): number {
    const count = Math.max(this.rows.length, 1);
    if (count === 1) {
      const singleBarPositionRatio = Number(this.data?.singleBarPositionRatio ?? 0.32);
      const clampedRatio = Math.min(Math.max(singleBarPositionRatio, 0.2), 0.5);
      return this.plotLeft + this.plotWidth * clampedRatio;
    }
    const step = count > 1 ? this.plotWidth / (count - 1) : 0;
    return this.plotLeft + index * step;
  }

  yForValue(value: number): number {
    const plotHeight = this.baselineY - this.plotTop;
    return this.baselineY - (value / this.maxValue) * plotHeight;
  }

  barTop(value: number): number {
    const height = this.barHeight(value);
    return height > 0 ? this.baselineY - height : this.baselineY;
  }

  barHeight(value: number): number {
    const height = Math.max(this.baselineY - this.yForValue(value), 0);
    const minHeight = Number(this.data?.barMinHeight ?? 4);
    return value > 0 ? Math.max(height, minHeight) : 0;
  }

  valueLabel(value: number): string {
    if (this.isMillionUnit()) {
      return this.formatMillionValue(value);
    }

    return this.formatCompactValue(value);
  }

  arrowPoints(): string {
    const x = this.lastBarCenterX;
    const y = this.arrowPointY;
    return `${x - this.arrowHalfWidth},${this.arrowStemTopY} ${x + this.arrowHalfWidth},${this.arrowStemTopY} ${x},${y}`;
  }

  trackByCategory(_: number, row: CagrChartRow): string {
    return row.category;
  }

  shouldShowValueLabel(value: number): boolean {
    return Math.abs(value) > 1e-9;
  }

  private toNumber(value: any): number {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  }

  private isMillionUnit(): boolean {
    const unit = String(this.data?.valueUnit || this.data?.salesUnit || '').trim().toLowerCase();
    return unit === 'million' || unit === 'millions' || unit === 'm';
  }

  private formatMillionValue(value: number): string {
    const roundedValue = Number(value.toFixed(2));
    const absolute = Math.abs(roundedValue);

    if (absolute < 1) {
      return `${roundedValue.toFixed(2)} M`;
    }

    return `${this.trimTrailingZeros(roundedValue.toFixed(2))} M`;
  }

  private formatCompactValue(value: number): string {
    const absolute = Math.abs(value);
    if (absolute >= 1_000_000) {
      return `${this.trimNumber(value / 1_000_000)}M`;
    }

    if (absolute >= 1_000) {
      return `${this.trimNumber(value / 1_000)}K`;
    }

    return this.trimNumber(value);
  }

  private trimNumber(value: number): string {
    const absolute = Math.abs(value);
    const decimals = absolute > 0 && absolute < 10 ? 1 : 0;
    return Number(value.toFixed(decimals)).toString();
  }

  private trimTrailingZeros(value: string): string {
    return value.replace(/\.0+$|(\.\d*?[1-9])0+$/, '$1');
  }

  private getNiceAxisMax(value: number): number {
    if (!Number.isFinite(value) || value <= 0) {
      return 1;
    }

    const exponent = Math.floor(Math.log10(value));
    const magnitude = Math.pow(10, exponent);
    const normalized = value / magnitude;
    const niceNormalized = normalized <= 1
      ? 1
      : normalized <= 2
        ? 2
        : normalized <= 5
          ? 5
          : 10;

    return Number((niceNormalized * magnitude).toPrecision(12));
  }
}
